import React, {useEffect, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import {CheckboxForm} from "../../../../../base/checkbox/checkbox";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import Form from "../../../../../base/form";
import {Divider, Fade} from "@mui/material";
import Button from "../../../../../base/button/button";
import IconButton from "../../../../../base/button/icon_button";
import {OpenInNew} from "@mui/icons-material";
import {openLinkInNewTab} from "../../../../../../../core/services/utils/utils";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import RGAInput from "../../../../../base/input/rga-input";
import {OutlinedDropdownForm} from "../../../../../base/dropdown/outlined_dropdown";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import ProductInformationPopover from "../../../../../app-specific/draggable-popover/product-information-popover";
import {MuiRFF, Yup} from '../../../../../form';
import CustomerAutocomplete from "../../../../../text-fields/autocomplete/customer-autocomplete";
import {AddPartSupportRequestProcessSteps} from "../../index";

const schema = Yup.object().shape({
    manualRga: Yup.boolean().default(true).nullable(),
    inventoryBased: Yup.boolean().default(false).required(ValidateMessages.required),
    rga: Yup.string().trim().when('manualRga', {
        is: (val) => !!val,
        then: Yup.string().trim().nullable().required(ValidateMessages.required),
        otherwise: Yup.string().nullable(),
    }),
    type: Yup.mixed().nullable().required(ValidateMessages.required),
    customer: Yup.mixed().when('warehouse', {
        is: val => !!val,
        then: Yup.mixed().nullable(),
        otherwise: Yup.mixed().nullable().required(ValidateMessages.required)
    }),
    warehouse: Yup.mixed().when('customer', {
        is: val => !!val,
        then: Yup.mixed().nullable(),
        otherwise: Yup.mixed().nullable().required(ValidateMessages.required)
    }),
}, [['customer', 'warehouse'], ['warehouse', 'customer']]);

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const AddPartSupportRequestDefineSection = ({
                                                setProcess,
                                                defineValues,
                                                setDefineValues,
                                                types,
                                                loadingTypes,
                                                warehouses,
                                                setOpen,
                                            }) => {
    const [initialValues, setInitialValues] = useState({});
    const [validatingRga, setValidatingRga] = useState(false)
    const [duplicateRGA, setDuplicateRGA] = useState();
    const [duplicatedRGAs, setDuplicatedRGAs] = useState([]);
    const rgaInput = useRef();
    const isMounted = useIsMounted();

    /**
     * Listens for the changes in defineValues and with each change:
     * - sets the initial values of the form.
     */
    useEffect(() => {
        setInitialValues({
            ...defineValues
        })
        rgaInput.current?.resetValidation();
    }, [defineValues])

    /**
     * Sets the selected request type then submits the form
     * @param {formApi} form
     * @param {any} type
     */
    const setTypeAndSubmitForm = (type, form) => {
        form.change('type', type);
        setTimeout(() => {
            form.submit()?.then();
        }, 300)
    }

    /**
     * Sets the defined values with the current form values and then proceeds to the lookup section of the dialog.
     */
    const proceedToNextStep = (values) => {
        setDefineValues({...values});
        setProcess(AddPartSupportRequestProcessSteps.lookup);
    }

    /**
     * If checked sets the manualRga to true and removes the rga from values of form, otherwise substitutes the rga
     * with its initial value.
     * @param e
     * @param form
     */
    const onManualRgaChanged = (e, form) => {
        if (!e.target.checked) {
            form.change('rga', null);
        } else {
            form.change('rga', initialValues.rga)
        }
        setDuplicateRGA(undefined);
        rgaInput.current?.resetValidation();
        form.change(e.target.name, e.target.checked)
        setTimeout(() => {
            if (!isMounted())
                return;
            form.resetFieldState('rga');
            rgaInput.current?.resetValidation();
        }, 100)
    }


    /**
     * If checked, removes the customer value of form, otherwise removes the warehouse value from form.
     * @param e
     * @param form
     */
    const onInventoryBasedChanged = (e, form) => {
        if (e.target.checked) {
            form.change('customer', null);
            form.change('warehouse', initialValues?.warehouse);
        } else {
            form.change('warehouse', null)
            form.change('customer', initialValues.customer)
        }
        form.change(e.target.name, e.target.checked)
    }

    /**
     * Opens the part support request associated with the duplicated RGA.
     */
    const openPartSupportRequestInNewTab = () => {
        const newTabUrl = window.location.origin + routeFunctions.warrantyAndReturn.single(duplicateRGA?.id);
        openLinkInNewTab(newTabUrl);
    }

    /**
     * Changes the RGA field's value. If the value can be found in the duplicatedRGAs then sets the duplicated rga
     * @param e {InputEvent}
     * @param form {any}
     */
    const onRGAChanged = (e, form) => {
        const found = duplicatedRGAs?.find((rga) => rga.rga === e.target.value);
        if (found) {
            setDuplicateRGA(found);
        }
        form.change(e.target.name, e.target.value);
    }

    /**
     * Sets the state associated with the found duplicated rga
     * @param {string} rga the rga
     */
    const onDuplicateFound = (rga) => {
        setDuplicateRGA(rga);
        setDuplicatedRGAs(prevState => Array.from(new Set([...prevState, rga]).values()))
    }

    return (
        <div className='mt--4'>
            <Form
                className='w-100'
                onSubmit={proceedToNextStep}
                validate={validate}
                initialValues={initialValues}
                render={({values, form, submitting}) => {
                    const submitButtonDisabled = validatingRga ||
                        submitting ||
                        (!values.customer && !values.inventoryBased) ||
                        (!values.warehouse && values.inventoryBased) ||
                        (values?.manualRga && !values.rga?.length);
                    return (
                        <Row className='no-gutters justify-content-center align-items-center w-100 mt-4'>
                            <Col xs={3} className={'row no-gutters justify-content-center'}>
                                <CheckboxForm
                                    onChange={(e) => onManualRgaChanged(e, form)}
                                    name={'manualRga'}
                                    required={required.manualRga}
                                    className={'manual-rga-check-box'}
                                    data={{
                                        value: values?.manualRga, label: 'Manual RGA',
                                    }}
                                />
                            </Col>
                            <Col xs={9} className={'px-3 pr-4'}>
                                <Fade in={values?.manualRga} unmountOnExit mountOnEnter>
                                    <div className={'w-100'}>
                                        <RGAInput
                                            name={'rga'}
                                            required={required.rga}
                                            value={values?.rga ?? ''}
                                            onChange={(e) => onRGAChanged(e, form)}
                                            onDuplicateFound={onDuplicateFound}
                                            onValidFound={() => setDuplicateRGA(undefined)}
                                            manual={values?.manualRga}
                                            setValidating={setValidatingRga}
                                            ref={rgaInput}
                                        />
                                    </div>
                                </Fade>
                            </Col>
                            <Col xs={12} className={'px-3'}>
                                <Fade in={!!duplicateRGA} unmountOnExit mountOnEnter>
                                    <div
                                        className={'rounded-xxl mt-3 p-3 d-flex align-items-center' +
                                            ' justify-content-between background-secondary-color-4'}>
                                        <p className={'duplicate-rga mr-2'}>
                                            <span className={'blue'}>
                                                {duplicateRGA?.type ?? ''}
                                            </span>
                                            request for invoice
                                            <span>
                                                {duplicateRGA?.invoiceNo ?? ''}
                                            </span>
                                            and part number
                                            <ProductInformationPopover
                                                partNo={duplicateRGA?.partNo}
                                                wrapperProps={{
                                                    className: 'part-number-fluid d-inline-block',
                                                    component: 'p',
                                                }}
                                                buttonProps={{
                                                    className: 'duplicate-rga-span'
                                                }}
                                            >
                                                {duplicateRGA?.partNo ?? ''}
                                            </ProductInformationPopover>
                                            with {duplicateRGA?.serialNo?.length ? 'serial number' : 'quantity of'}
                                            <span>
                                                {duplicateRGA?.serialNo?.length ? duplicateRGA?.serialNo : duplicateRGA?.unSeriliazablePartsCount ?? '0'}
                                            </span>
                                            and status of
                                            <span>
                                                {duplicateRGA?.status ?? ''}
                                            </span>
                                        </p>
                                        <IconButton
                                            color={'secondary'}
                                            onPressed={openPartSupportRequestInNewTab}
                                        >
                                            <OpenInNew className='icon normal'/>
                                        </IconButton>
                                    </div>
                                </Fade>
                            </Col>
                            <Col xs={12} className={'px-5'}>
                                <div className={'w-100 px-1'}>
                                    <CheckboxForm
                                        onChange={(e) => onInventoryBasedChanged(e, form)}
                                        name={'inventoryBased'}
                                        required={required.inventoryBased}
                                        data={{
                                            value: values.inventoryBased, label: "Select From Inventory"
                                        }}
                                    />
                                </div>
                            </Col>
                            <div className={'w-100 no-gutters mx-4 d-flex flex-wrap mb-5 pl-4 mt-3'}>
                                {
                                    values?.inventoryBased &&
                                    <div className={'w-100'}>
                                        <OutlinedDropdownForm
                                            label={'Warehouse'}
                                            name={'warehouse'}
                                            required={required.warehouse}
                                            renderValue={v => v?.name ?? ''}
                                            data={warehouses?.map(e => ({
                                                label: e.name,
                                                value: e,
                                                id: e.id,
                                            })) ?? []}
                                        />
                                    </div>
                                }
                                {
                                    !values?.inventoryBased &&
                                    <CustomerAutocomplete
                                        form
                                        name={'customer'}
                                        fullWidth
                                        required={required.customer}
                                        label={'Customer'}
                                    />
                                }
                            </div>
                            <Divider className={'divider'}/>
                            <Row className='no-gutters footer justify-content-between align-items-start w-100'>
                                <Button
                                    variant={'dialog'}
                                    color={'primary'}
                                    disabled={submitting || loadingTypes}
                                    onClick={() => setOpen(false)}
                                    borderRadius={'0 8px 0 8px'}
                                >
                                    {
                                        loadingTypes
                                            ? 'Loading'
                                            : `Close`
                                    }
                                </Button>
                                <div className={'d-flex align-items-center justify-content-end'}>
                                    {types?.map((type, index, array) => (
                                        <Button
                                            key={type.id}
                                            disabled={submitButtonDisabled}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setTypeAndSubmitForm(type, form)}
                                            borderRadius={
                                                array.length === 1
                                                    ? '10px 0 10px 0'
                                                    : index === array.length - 1
                                                        ? '0 0 10px 0'
                                                        : index === 0
                                                            ? '10px 0 0 0'
                                                            : '0 0 0 0'
                                            }
                                        >
                                            {
                                                loadingTypes
                                                    ? 'Loading'
                                                    : submitting
                                                        ? "Submitting..."
                                                        : `Submit ${type.title}`
                                            }
                                        </Button>
                                    ))}
                                </div>
                            </Row>
                        </Row>
                    );
                }}
            />
        </div>
    );
}

export default AddPartSupportRequestDefineSection;
